/**
 * MicPermissionDeniedCard — 1.135.0 structured iOS guidance for NotAllowedError.
 *
 * Fires when status='error' AND error contains "NotAllowedError" AND the user
 * is on iOS Safari. This is the case AFTER Lockdown Mode is excluded (or was
 * never on): the user previously denied the in-page permission prompt and
 * iOS persists that denial. iOS does NOT show any system overlay for this
 * case (researched 2026-05-14), so the web app must surface its own guidance.
 *
 * iOS 26 Settings reorganization: Safari moved under `Settings → Apps → Safari`
 * (Apps subnav introduced in iOS 18.2, retained in iOS 26). Permission options
 * remained Ask/Deny/Allow.
 *
 * Compact toolbar default in iOS 26 replaced the "Aa" address-bar button with
 * "•••". Top toolbar layout (legacy look) keeps "Aa" on the left of URL.
 *
 * Sources:
 *   - Apple Support, Browse the web privately in Safari on iPhone (iOS 26):
 *     https://support.apple.com/guide/iphone/browse-the-web-privately-iphb01fc3c85/ios
 *   - 9to5mac (2025-09-15) — iOS 26 Safari Compact toolbar "•••" button:
 *     https://9to5mac.com/2025/09/15/iphone-ios-26-safari-new-compact-design/
 *   - webtrickz / GeekChamp confirming `Settings → Apps → Safari` path on iOS 26.
 */
import { useState } from 'react';

export function MicPermissionDeniedCard() {
  const [fastOpen, setFastOpen] = useState(false);
  const [troubleOpen, setTroubleOpen] = useState(false);
  // 1.136.0 — dismiss after reading; reload restores if condition persists.
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <div
      data-testid="voice-permission-denied-card"
      className="rounded-md border border-vibe-error bg-vibe-bg-tertiary text-vibe-text text-xs p-3 space-y-2"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start justify-between gap-2">
        <div
          data-testid="voice-permission-denied-title"
          className="font-semibold text-vibe-error flex items-center gap-2"
        >
          <span aria-hidden="true">🎤</span>
          <span>Microphone denied for this site</span>
        </div>
        <button
          type="button"
          data-testid="voice-permission-denied-close"
          aria-label="dismiss microphone-denied notice"
          onClick={() => setDismissed(true)}
          className="text-vibe-text-secondary hover:text-vibe-text -mr-1 -mt-1 px-2 py-1 rounded [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
        >
          ×
        </button>
      </div>

      <ol className="space-y-1 pl-4 list-decimal text-vibe-text">
        <li data-testid="voice-permission-denied-step-1">
          Open the iPhone <strong>Settings</strong> app, scroll down, tap <strong>Apps</strong>.
        </li>
        <li data-testid="voice-permission-denied-step-2">
          Tap <strong>Safari</strong> → scroll to <strong>&ldquo;Settings for Websites&rdquo;</strong> → tap <strong>Microphone</strong>.
        </li>
        <li data-testid="voice-permission-denied-step-3">
          Find this site in the list and set it to <strong>Allow</strong>
          (or change top toggle to <strong>Ask</strong>), then return to Safari and <strong>reload</strong>.
        </li>
      </ol>

      <div className="flex gap-3 pt-1 flex-wrap">
        <button
          type="button"
          data-testid="voice-permission-denied-fast-toggle"
          onClick={() => setFastOpen((v) => !v)}
          className="text-vibe-accent underline-offset-2 hover:underline"
          aria-expanded={fastOpen}
        >
          {fastOpen ? 'Hide fast path' : 'Faster path in Safari'}
        </button>
        <button
          type="button"
          data-testid="voice-permission-denied-trouble-toggle"
          onClick={() => setTroubleOpen((v) => !v)}
          className="text-vibe-accent underline-offset-2 hover:underline"
          aria-expanded={troubleOpen}
        >
          {troubleOpen ? 'Hide troubleshooting' : 'Site not in the list?'}
        </button>
      </div>

      {fastOpen && (
        <div data-testid="voice-permission-denied-fast-content" className="pt-1 text-vibe-text-secondary">
          In Safari with this page open: tap the <strong>&ldquo;•••&rdquo;</strong> button next to the URL bar
          (Top toolbar layout / iOS 17–18: <strong>&ldquo;Aa&rdquo;</strong> button on the LEFT of the URL) → <strong>Website Settings</strong> → <strong>Microphone</strong> → <strong>Allow</strong>, then reload.
        </div>
      )}

      {troubleOpen && (
        <div data-testid="voice-permission-denied-trouble-content" className="pt-1 text-vibe-text-secondary">
          iOS adds the site to the Microphone list only AFTER Safari has actually prompted on it.
          If the site never prompted (because Lockdown Mode was blocking the mic API entirely),
          the list will be empty: first exclude this site from Lockdown Mode (Step 1 above
          path → Website Settings → turn OFF Lockdown Mode), reload, then attempt the mic — the iOS
          permission prompt will fire and the site will now appear in Settings → Apps → Safari → Microphone.
        </div>
      )}
    </div>
  );
}
